// src/data/worksheets/sleep.js — performance-side pair (authored).
// Built off the Sleep action plan: cool dark room, empty your head, morning light —
// plus fixed wake time, screens off and the caffeine cut-off.

export const SLEEP_CHECKLIST = {
    id: 'sleep_checklist', topicId: 'sleep', kind: 'checklist',
    title: 'Sleep Checklist',
    intro: 'Sleep affects reaction time, mood and recovery more than any other habit. Tick these off as you build them in.',
    sections: [
        { id: 'anchor', title: 'Anchor the Clock', prompts: [
            { id: 'wake', type: 'checklist', label: 'Fixed Wake Time', items: [
                { id: 'w1', label: 'Same wake-up time every day this week, including days off.', detail: 'Wake time is the anchor; bedtime follows it.' },
                { id: 'w2', label: 'Get 10–15 minutes of daylight within half an hour of waking. It sets your body clock for the night ahead.' }
            ] },
            { id: 'caffeine', type: 'checklist', label: 'Caffeine Cut-Off', items: [
                { id: 'c1', label: 'No caffeine within 10 hours of bedtime.' },
                { id: 'c2', label: 'Keep alcohol away from the last two hours — it fragments sleep even when it helps you drop off.' }
            ] }
        ] },
        { id: 'room', title: 'Set Up the Room', prompts: [
            { id: 'cool', type: 'checklist', label: 'Cool, Dark, Quiet', items: [
                { id: 'r1', label: 'Aim for 15–19°C and as dark as you can make it.', detail: 'A small drop in body temperature helps deep sleep start.' },
                { id: 'r2', label: 'Block noise if you can — earplugs or steady background sound.' },
                { id: 'r3', label: 'Charge the phone outside the bedroom, or face-down on Do Not Disturb.' }
            ] }
        ] },
        { id: 'winddown', title: 'Wind Down', prompts: [
            { id: 'head', type: 'checklist', label: 'Empty Your Head', items: [
                { id: 'h1', label: 'Write tomorrow\'s list down before you lie down so it stops running in your head.' },
                { id: 'h2', label: 'If something is bothering you, write one line about it and what you\'ll do tomorrow.' }
            ] },
            { id: 'screens', type: 'checklist', label: 'Screens Off 30 Minutes Before', items: [
                { id: 's1', label: 'Swap the phone for something low-key in the last half hour.' },
                { id: 's2', label: 'Dim the lights an hour before bed.' }
            ] },
            { id: 'routine', type: 'checklist', label: 'A Repeatable Routine', items: [
                { id: 'rt1', label: 'Same three steps every night (e.g., shower, stretch, read). The body learns the cue.' },
                { id: 'rt2', label: 'Keep it to 20–30 minutes.' }
            ] }
        ] },
        { id: 'trouble', title: 'When You Can\'t Sleep', prompts: [
            { id: 'rules', type: 'checklist', label: 'Rules for Bad Nights', items: [
                { id: 'b1', label: 'Awake for more than 20 minutes? Get up, do something dull in dim light, come back when sleepy.' },
                { id: 'b2', label: 'Don\'t check the time. Every check is a stress hit.' },
                { id: 'b3', label: 'A bad night is survivable — keep the wake time anyway. Napping late or sleeping in makes the next night worse.' }
            ] },
            { id: 'naps', type: 'checklist', label: 'Naps', items: [
                { id: 'n1', label: 'If you nap, keep it to 20 minutes and before mid-afternoon.' }
            ] }
        ] },
        { id: 'review', title: 'Review & Reflect', prompts: [
            { id: 'weekly', type: 'checklist', label: 'Weekly', items: [
                { id: 'wk1', label: 'Look at the week\'s hours and quality. What was different on the best nights?' },
                { id: 'wk2', label: 'Change one thing for next week.' }
            ] },
            { id: 'notes', type: 'text', label: 'Notes', multiline: true }
        ] }
    ],
    outro: 'Consistency beats catching up. The wake time is the anchor — protect it and the rest follows.',
    supportNote: 'If sleep has been poor for weeks despite the basics, it is worth talking to a professional about it.'
};

export const SLEEP_WORKSHEET = {
    id: 'sleep_worksheet', topicId: 'sleep', kind: 'worksheet',
    title: 'Sleep Worksheet',
    intro: 'Answer in your own words. Nothing here is scored, and only you can see it.',
    sections: [
        { id: 'self', title: 'Self Assessment', prompts: [
            { id: 'quality', type: 'scale', label: 'How well have you slept over the past week?', min: 1, max: 10, minLabel: 'Wrecked', maxLabel: 'Fully rested' },
            { id: 'hours', type: 'text', label: 'Typical hours on a weeknight / on a night off', help: 'Two numbers — the gap between them tells you a lot.' },
            { id: 'problems', type: 'list', label: 'What gets in the way?', help: 'e.g., can\'t switch off, waking at 3am, {late training|late sessions}, screens, caffeine, a busy mind.', count: 3 },
            { id: 'effects', type: 'text', label: 'How does poor sleep show up in your day?', help: 'Reaction time, mood, focus, appetite, motivation.', multiline: true }
        ] },
        { id: 'understanding', title: 'Understanding Your Sleep', prompts: [
            { id: 'best', type: 'text', label: 'Your best night recently — what was different?', help: 'Timing, room, food, screens, stress. This is your template.', multiline: true },
            { id: 'evening', type: 'text', label: 'What your evening currently looks like from 8pm', help: 'Honestly, hour by hour.', multiline: true },
            { id: 'thoughts', type: 'list', label: 'What runs through your head when you can\'t sleep', count: 3 }
        ] },
        { id: 'building', title: 'Building the Routine', prompts: [
            { id: 'waketime', type: 'text', label: 'Your fixed wake time (every day)' },
            { id: 'bedtime', type: 'text', label: 'Your target lights-out', help: 'Wake time minus the hours you need.' },
            { id: 'cutoff', type: 'text', label: 'Your caffeine cut-off time' },
            { id: 'routine', type: 'list', label: 'Your three-step wind-down', count: 3, itemLabels: ['Step 1', 'Step 2', 'Step 3'] },
            { id: 'room', type: 'list', label: 'Room fixes', help: 'Temperature, light, noise, phone.', count: 3 }
        ] },
        { id: 'plan', title: 'Action Plan', prompts: [
            { id: 'goal', type: 'text', label: 'This week\'s sleep goal', help: 'e.g., "same wake time all 7 days", "screens off by 10".' },
            { id: 'badnight', type: 'text', label: 'Your bad-night rule', help: 'What you will do if you are awake for 20 minutes.' },
            { id: 'support', type: 'text', label: 'Who can help you protect it', help: 'A partner, a roommate, a {teammate|colleague} on the same schedule.' }
        ] },
        { id: 'track', title: 'Track It', description: 'Bedtime, wake time, hours and quality. Two weeks of this shows you exactly what helps.', prompts: [
            { id: 'tracker', type: 'table', label: 'Sleep log', columns: [
                { id: 'day', label: 'Day', kind: 'date' },
                { id: 'bed', label: 'Lights out', kind: 'text' },
                { id: 'wake', label: 'Wake time', kind: 'text' },
                { id: 'hours', label: 'Hours', kind: 'number', min: 0, max: 14 },
                { id: 'quality', label: 'Quality (1–10)', kind: 'number', min: 1, max: 10 },
                { id: 'notes', label: 'Notes (caffeine, screens, stress)', kind: 'text' }
            ], maxRows: 31, chart: { x: 'day', series: [{ id: 'quality', label: 'Quality' }, { id: 'hours', label: 'Hours' }] } }
        ] }
    ],
    outro: 'Protect the wake time, cool the room, empty your head. Everything else is a bonus.',
    supportNote: 'If sleep has been poor for weeks despite the basics, it is worth talking to a professional about it.'
};

export const SLEEP_WORKSHEETS = [SLEEP_CHECKLIST, SLEEP_WORKSHEET];
